import api from './api'
import { API_ENDPOINTS } from '../config/api'
import { getDeviceId } from '../utils/deviceId'

// Every credential-bearing call carries the browser's stable device id so the
// backend's single-session check treats a re-login from THIS browser as the
// same session instead of kicking the tab that is already open. See
// utils/deviceId.js for why.
const withDeviceId = (payload) => ({ ...payload, deviceId: getDeviceId() })

export const authService = {
  login: async (credentials) => {
    const response = await api.post(API_ENDPOINTS.AUTH.LOGIN, withDeviceId(credentials))
    return response.data
  },

  logout: async () => {
    const response = await api.post(API_ENDPOINTS.AUTH.LOGOUT)
    return response.data
  },

  refreshToken: async () => {
    const response = await api.post(API_ENDPOINTS.AUTH.REFRESH)
    return response.data
  },

  getProfile: async () => {
    const response = await api.get(API_ENDPOINTS.USER.PROFILE)
    return response.data
  },

  ownerLogin: async (credentials) => {
    const response = await api.post(API_ENDPOINTS.AUTH.OWNER_LOGIN, withDeviceId(credentials))
    return response.data
  },

  // Executive (multi-login) accounts — the response already carries the safe
  // executive object + permissions, so no profile call follows this
  executiveLogin: async (credentials) => {
    const response = await api.post(API_ENDPOINTS.MULTI_LOGIN.LOGIN, credentials)
    return response.data
  },

  getOwnerProfile: async () => {
    const response = await api.get(API_ENDPOINTS.USER.OWNER_PROFILE)
    return response.data
  },

  updatePlatformConfigurations: async (data) => {
    const response = await api.post(API_ENDPOINTS.AUTH.UPDATE_PLATFORM_CONFIG, data)
    return response.data
  },

  // 2FA Methods
  // 2FA verification issues the REAL token, so it stamps login_id too — it
  // needs the device id just as much as the login call does.
  verifyStaff2FA: async (data) => {
    const response = await api.post(API_ENDPOINTS.AUTH.VERIFY_2FA_STAFF, withDeviceId(data))
    return response.data
  },

  verifyOwner2FA: async (data) => {
    const response = await api.post(API_ENDPOINTS.AUTH.VERIFY_2FA_OWNER, withDeviceId(data))
    return response.data
  },

  // Re-send the login OTP. No token exists yet, so the id from the login
  // response is the only thing identifying the account — no credentials are
  // kept client-side for this.
  resendStaff2FA: async (staffId) => {
    const response = await api.post(API_ENDPOINTS.AUTH.RESEND_2FA_STAFF, { staffId })
    return response.data
  },

  resendOwner2FA: async (ownerId) => {
    const response = await api.post(API_ENDPOINTS.AUTH.RESEND_2FA_OWNER, { ownerId })
    return response.data
  },

  generate2FALink: async () => {
    const response = await api.post(API_ENDPOINTS.AUTH.GENERATE_2FA_LINK)
    return response.data
  },

  changePassword: async (data, role) => {
    const endpoint = role === 'owner'
      ? API_ENDPOINTS.AUTH.OWNER_UPDATE_PASSWORD
      : API_ENDPOINTS.AUTH.STAFF_UPDATE_PASSWORD
    const response = await api.post(endpoint, data)
    return response.data
  },

  acceptRules: async (role, id) => {
    const endpoint = role === 'owner'
      ? API_ENDPOINTS.AUTH.ACCEPT_RULES_OWNER
      : API_ENDPOINTS.AUTH.ACCEPT_RULES_STAFF
    const payload = role === 'owner' ? { owner_id: id } : { staff_id: id }
    const response = await api.post(endpoint, payload)
    return response.data
  },

  verifyPassword: async (password) => {
    const response = await api.post(API_ENDPOINTS.AUTH.VERIFY_PASSWORD, { password })
    return response.data
  },

  sendDisable2FACode: async () => {
    const response = await api.post(API_ENDPOINTS.AUTH.SEND_DISABLE_2FA_CODE)
    return response.data
  },

  disable2FA: async (data) => {
    const response = await api.post(API_ENDPOINTS.AUTH.DISABLE_2FA, data)
    return response.data
  },
}
